import type { Access, Me, Price } from '@wellread/api';
import { count } from '@wellread/core';

// How paying is worded. A reader has some days free, then pays for a month
// at a time. A month is paid for once and runs out: nothing renews. A month
// paid for is Premium: its books use no credits.

/**
 * Where the reader stands, or undefined when wellread isn't asking to be
 * paid: a server that takes no payment says nothing, and so does a payload
 * cached before it took any.
 */
export const accessOf = (me?: Pick<Me, 'access'>): Access | undefined => me?.access ?? undefined;

/** True when the reader is past their free days and paid months, on the server's word. */
export const ended = (a: Access | undefined): boolean => a?.state === 'ended';

/** True in a month the reader has paid for: analysing a book uses no credit. */
export const hasPremium = (a: Pick<Access, 'state'> | undefined): boolean => a?.state === 'paid';

/** True where Premium is on offer to a reader who doesn't have it: wellread takes payment, and they are in their free days. */
export const offersPremium = (a: Pick<Access, 'state'> | undefined): boolean => a?.state === 'trial';

/** What Premium gives, on its page: a name and a line each. Every line is something the app does today. */
export const PREMIUM = [
  {
    name: 'As many books as you want',
    line: 'Bring every EPUB and PDF you own. wellread analyses each one, and none uses a credit.',
  },
  {
    name: 'Unlimited recall',
    line: 'Questions on what you have read, as often as you like. Ideas come back just before you’d forget them.',
  },
  {
    name: 'Ask AI about your books',
    line: 'Answers drawn from your own books, each pointing to the page it came from.',
  },
  {
    name: 'A map of what you know',
    line: 'Every book adds its ideas, and they link across books.',
  },
] as const;

/**
 * True when a copy of the reader's access says they may read, and its end
 * has passed since the copy was fetched: it is out of date, and the server
 * is asked again. A copy fetched after its own end is the server's word
 * against this device's clock, and the server's stands.
 */
export function outOfDate(a: Access | undefined, fetchedAt: number, now = Date.now()): boolean {
  if (!a || a.state === 'ended') return false;
  const until = Date.parse(a.until);
  return until <= now && fetchedAt < until;
}

const DAY = 24 * 60 * 60 * 1000;

/** Days left, a part of a day counting as one; none once it has ended. */
export function daysLeft(a: Pick<Access, 'until'>, now = Date.now()): number {
  return Math.max(0, Math.ceil((Date.parse(a.until) - now) / DAY));
}

/** How long is left, in words: "5 days left", "1 day left", "ends today". */
export function left(a: Pick<Access, 'until'>, now = Date.now()): string {
  if (new Date(a.until).toDateString() === new Date(now).toDateString()) return 'ends today';
  const n = daysLeft(a, now);
  return n === 1 ? '1 day left' : `${count(n)} days left`;
}

/** The last days of what a reader has: when Home says it is running out. */
export const RUNNING_OUT_DAYS = 3;

/** True in the last days of the free days or of a paid month. */
export const runningOut = (a: Access | undefined, now = Date.now()): boolean =>
  !!a && a.state !== 'ended' && Date.parse(a.until) - now <= RUNNING_OUT_DAYS * DAY;

const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

/** The day something ends or ended: "20 Oct", with the year when it isn't this one ("3 Jan 2027"). */
export function day(iso: string, now = Date.now()): string {
  const d = new Date(iso);
  const short = `${d.getDate()} ${months[d.getMonth()]}`;
  return d.getFullYear() === new Date(now).getFullYear() ? short : `${short} ${d.getFullYear()}`;
}

/** What a reader can pay for at a time. */
export type Plan = 'month' | 'year';

/** The two, in the order the page offers them, as it names them. */
export const PLANS: { plan: Plan; name: string }[] = [
  { plan: 'month', name: 'Monthly' },
  { plan: 'year', name: 'Yearly' },
];

/**
 * What a year comes to a month, to the rupee, and how much less that is than
 * paying by the month, in whole percent: absent when a year saves nothing.
 */
export function yearDeal(month: Price, year: Price): { perMonth: Price; saving?: number } {
  const perMonth = { amount: Math.round(year.amount / 12 / 100) * 100, currency: year.currency };
  const saving = Math.floor((1 - year.amount / (12 * month.amount)) * 100);
  return saving > 0 ? { perMonth, saving } : { perMonth };
}

/** A price as the reader pays it: "₹499", "₹499.50", "$5". */
export function price(p: Price): string {
  const whole = p.amount % 100 === 0;
  return new Intl.NumberFormat('en-IN', {
    style: 'currency',
    currency: p.currency,
    minimumFractionDigits: whole ? 0 : 2,
    maximumFractionDigits: whole ? 0 : 2,
  }).format(p.amount / 100);
}

/**
 * True for a reader who could be shown Premium before their first book: in
 * their free days, never having paid.
 */
export const mayBeShownPremium = (a: Access | undefined): boolean => a?.state === 'trial' && !a.has_paid;

/**
 * True for a reader the landing page shows Premium to before their first
 * book: one who could be, with no book of their own yet. One of wellread's
 * own short books isn't theirs.
 */
export const showsPremiumFirst = (a: Access | undefined, books: readonly { starter?: boolean }[]): boolean =>
  mayBeShownPremium(a) && books.every((b) => b.starter === true);

/**
 * What Home does about Premium before anything of it shows. 'go': the reader
 * is taken to Premium's page in Home's place. 'wait': the shelf hasn't said
 * yet whether they have a book, so nothing shows and Home never flashes
 * before that page. 'stay': Home. A shelf that can't be had (no connection,
 * or it failed) isn't waited for: Home opens.
 */
export function premiumFirst(
  a: Access | undefined,
  shown: boolean,
  shelf: { books?: readonly { starter?: boolean }[]; loading: boolean },
): 'go' | 'wait' | 'stay' {
  if (shown || !mayBeShownPremium(a)) return 'stay';
  if (shelf.books) return showsPremiumFirst(a, shelf.books) ? 'go' : 'stay';
  return shelf.loading ? 'wait' : 'stay';
}

/**
 * This device's memory that a reader has been shown Premium from the landing
 * page: it is shown once, and after that they go to it themselves. Kept by
 * reader: another account on the same device is still shown it.
 */
const shownKey = (reader: string) => `wellread.premium-shown:${reader}`;

// Where there is no storage to keep it in, it holds for this visit.
const shownThisVisit = new Set<string>();

/** True once this reader has been shown Premium from the landing page here. */
export function shownPremium(reader: string): boolean {
  if (shownThisVisit.has(reader)) return true;
  try {
    return localStorage.getItem(shownKey(reader)) === '1';
  } catch {
    return false;
  }
}

/** Remembers that this reader has been shown Premium from the landing page. */
export function markPremiumShown(reader: string): void {
  shownThisVisit.add(reader);
  try {
    localStorage.setItem(shownKey(reader), '1');
  } catch {
    // Nothing to keep it in: it holds for this visit.
  }
}

/** What the paywall is headed, by what ran out. */
export const endedHeading = (a: Pick<Access, 'has_paid'>) =>
  a.has_paid ? 'Your month is over' : 'Your free days are over';

/** When it ran out, as the paywall's next sentence: "They ended on 20 Oct." */
export const endedOn = (a: Pick<Access, 'until' | 'has_paid'>, now = Date.now()) =>
  `${a.has_paid ? 'It' : 'They'} ended on ${day(a.until, now)}.`;

/** Where a reader writes about a payment. */
export const PAYMENTS_MAIL = 'hello@wellread.ink';
